import { forwardRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallback?: React.ReactNode;
  fallbackClassName?: string;
  loadingClassName?: string;
}

const Image = forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      className,
      alt,
      fallback,
      fallbackClassName,
      loadingClassName,
      onError,
      ...props
    },
    ref
  ) => {
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const handleError = (
      event: React.SyntheticEvent<HTMLImageElement, Event>
    ) => {
      setHasError(true);
      onError?.(event);
    };

    const handleLoad = () => {
      setIsLoading(false);
    };

    if (hasError) {
      return (
        <div
          className={cn(
            'flex items-center justify-center bg-grayscale-50',
            fallbackClassName
          )}
        >
          {fallback || (
            <div className="flex flex-col items-center gap-2 text-grayscale-400">
              <i className="ti ti-photo-off text-2xl" />
              <span className="text-xs">Image not found</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <>
        {isLoading && (
          <div
            className={cn(
              'flex items-center justify-center bg-grayscale-50 w-full h-full',
              loadingClassName
            )}
          >
            <Spinner />
          </div>
        )}
        <img
          ref={ref}
          className={cn(isLoading && 'hidden', className)}
          alt={alt}
          onError={handleError}
          onLoad={handleLoad}
          {...props}
        />
      </>
    );
  }
);

Image.displayName = 'Image';

export { Image };
